import React from 'react';
import _ from 'lodash';
import moment from 'moment';
import { numberWithComma } from 'src/utils/Utils';

import BreadCrumb from 'jbc-front/components/BreadCrumb';
import { CLIENT_SETTINGS, EDIT_CLIENT_SETTING_USING_PLAN } from 'src/constants/EndpointUrls';
import { PLAN_TYPES, isTrialPlan, isPaidPlan } from 'src/reducers/clients/currentClient';
import Button from 'jbc-front/components/Button';
import CreditCardInfo from 'jbc-front/components/CreditCardInfo';
import styles from './ShowClientPaidPlan.scss';
import ChangePaymentMethod from './ChangePaymentMethod';
import { useTranslation } from 'react-i18next';

const MonthlyBillingRows = ({ monthlyBillings }) => {
  const { t } = useTranslation();
  if (_.isEmpty(monthlyBillings)) {
    return (
      <tr>
        <td colSpan="5">{t('features.ClientPaidPlan.components.ShowClientPaidPlan.no_invoice_history')}</td>
      </tr>
    );
  }
  return monthlyBillings.map(monthlyBilling => (
    <tr key={monthlyBilling.id}>
      <td>{moment(monthlyBilling.dateOn).format('YYYY年M月')}</td>
      <td>¥{numberWithComma(monthlyBilling.basePrice)}</td>
      <td>{monthlyBilling.chargeableEmployeesCount}人</td>
      <td>¥{numberWithComma(monthlyBilling.price)}</td>
      <td>¥{numberWithComma(monthlyBilling.totalPrice)}</td>
    </tr>
  ));
};

const ShowClientPaidPlan = ({
  trialFinishAt,
  planType,
  planControllable,
  paymentMethodType,
  cards,
  employeeEmail,
  monthlyBillings
}) => {
  const { t } = useTranslation();
  return (
    <div>
      <div className="l-main-title-wrap">
        <h1 className="m-title-main">{t('features.ClientPaidPlan.components.ShowClientPaidPlan.plan_in_use')}</h1>
      </div>
      <div className="l-wrap-m l-contents-wrap">
        <div className="u-mb20">
          <BreadCrumb>
            <BreadCrumb.Item label={t('components.SideMenu.SideMenu.setting')} path={CLIENT_SETTINGS} />
            <BreadCrumb.Item label={t('features.ClientPaidPlan.components.ShowClientPaidPlan.plan_in_use')} />
          </BreadCrumb>
        </div>

        <div className={styles.box}>
          <div className={styles.boxHead}>{t('features.ClientPaidPlan.components.ShowClientPaidPlan.plan_in_use')}</div>
          <div className={styles.boxBody}>
            <div className={styles.plans}>
              <div className={styles.plansName}>{PLAN_TYPES[planType]}</div>
              {isTrialPlan(planType) ? (
                <div className={styles.plansDate}>
                  {t('features.ClientPaidPlan.components.ShowClientPaidPlan.end_date_of_trial_period')}: <span>{moment(trialFinishAt).format('YYYY年MM月DD日')}</span>
                </div>
              ) : null}
            </div>
            {!isPaidPlan(planType) ? (
              <div>
                <p className={styles.plansNote}>
                  {t('features.ClientPaidPlan.components.ShowClientPaidPlan.after_the_trial_plan_ends_it_will_shift_to_the_free_plan_with_limited_function')}
                </p>
                <ul className={styles.plansNoteLists}>
                  <li>・{t('features.ClientPaidPlan.components.ShowClientPaidPlan.up_to_5_registered_employees')}</li>
                  <li>・{t('features.ClientPaidPlan.components.ShowClientPaidPlan.service_link_is_not_available')}</li>
                  <li>・{t('features.ClientPaidPlan.components.ShowClientPaidPlan.chat_support_is_not_available')}</li>
                </ul>
                <div className="u-mt10">
                  <a
                    href="https://payrolljp.jobcan.in.th/plan/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="u-txt-link"
                  >
                    {t('features.ClientPaidPlan.components.ShowClientPaidPlan.click_here_for_detail')}
                  </a>
                </div>
                <div className="u-mt50 u-mb20 u-ta-c">
                  <Button success large as="a" href={EDIT_CLIENT_SETTING_USING_PLAN}>
                    {t('features.ClientPaidPlan.components.ShowClientPaidPlan.click_here_for_paid_plan_application')}
                  </Button>
                </div>
              </div>
            ) : (
              planControllable && (
                <div>
                  <div className={styles.payments}>
                    <p className={styles.paymentsTitle}>
                      {t('features.ClientPaidPlan.components.ShowClientPaidPlan.payment_method')}
                      <ChangePaymentMethod employeeEmail={employeeEmail} />
                    </p>
                    <div className={styles.paymentsMethod}>{paymentMethodType}</div>
                    {cards &&
                      cards[0] && (
                        <div>
                          <div className="u-mb5 u-mt20">{t('features.ClientPaidPlan.components.ShowClientPaidPlan.card_information')}</div>
                          <CreditCardInfo
                            cardInfo={{ ...cards[0], exp_month: cards[0].expMonth, exp_year: cards[0].expYear }}
                            className={styles.cardInfo}
                          />
                        </div>
                      )}
                  </div>
                </div>
              )
            )}
          </div>
        </div>

        {isPaidPlan(planType) && planControllable ? (
          <div className={styles.billings}>
            <p className={styles.billingsTitle}>{t('features.ClientPaidPlan.components.ShowClientPaidPlan.invoice_information')}</p>
            <table className="m-table-list">
              <thead>
                <tr>
                  <th>{t('features.ClientPaidPlan.components.ShowClientPaidPlan.Year_Month')}</th>
                  <th>{t('features.ClientPaidPlan.components.ShowClientPaidPlan.unit_price')}</th>
                  <th>{t('features.ClientPaidPlan.components.ShowClientPaidPlan.number_of_user')}</th>
                  <th>{t('features.ClientPaidPlan.components.ShowClientPaidPlan.total_amount_tax_exclusive')}</th>
                  <th>{t('features.ClientPaidPlan.components.ShowClientPaidPlan.total_amount_tax_inclusive')}</th>
                </tr>
              </thead>
              <tbody>
                <MonthlyBillingRows monthlyBillings={monthlyBillings} />
              </tbody>
            </table>
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default ShowClientPaidPlan;
